import { MONEDA, cotizacionCreateSchema, cotizacionUpdateSchema } from "@uirtus/shared";
import { useState, type FormEvent } from "react";
import type { CotizacionConDetalle } from "../lib/cotizaciones";
import { EditorConceptos, type ConceptoLocal } from "./editor-conceptos";
import { SelectorCliente, type ClienteResumen } from "./selector-cliente";
import { SelectorProyecto, type ProyectoResumen } from "./selector-proyecto";
import { AreaTexto, Aviso, Boton, Campo, Entrada, Seleccion } from "./ui";

const conceptosDesdeCotizacion = (c: CotizacionConDetalle): ConceptoLocal[] =>
  c.conceptos.map((x) => ({
    clave: x.id,
    servicioId: x.servicioId ?? undefined,
    claveProdServ: x.claveProdServ,
    claveUnidad: x.claveUnidad,
    unidad: x.unidad ?? undefined,
    descripcion: x.descripcion,
    cantidad: x.cantidad,
    valorUnitario: x.valorUnitario,
    descuento: x.descuento,
    objetoImp: x.objetoImp,
    tasaIva: x.tasaIva,
  }));

interface Props {
  /** Presente = edición de una cotización en borrador. Ausente = alta. */
  cotizacion?: CotizacionConDetalle;
  enviando?: boolean;
  errorServidor?: string | null;
  alEnviar: (datos: Record<string, unknown>) => void;
  alCancelar: () => void;
}

export function FormularioCotizacion({
  cotizacion,
  enviando,
  errorServidor,
  alEnviar,
  alCancelar,
}: Props) {
  const [cliente, setCliente] = useState<ClienteResumen | null>(
    cotizacion
      ? {
          id: cotizacion.clienteId,
          rfc: cotizacion.cliente.rfc,
          razonSocial: cotizacion.cliente.razonSocial,
        }
      : null,
  );
  const [proyecto, setProyecto] = useState<ProyectoResumen | null>(
    cotizacion?.proyecto
      ? { id: cotizacion.proyecto.id, nombre: cotizacion.proyecto.nombre, clave: cotizacion.proyecto.clave }
      : null,
  );
  const [titulo, setTitulo] = useState(cotizacion?.titulo ?? "");
  // string, no Moneda: el <select> entrega texto plano y el esquema Zod es
  // quien decide si el valor es válido, igual que con el resto de catálogos.
  const [moneda, setMoneda] = useState<string>(cotizacion?.moneda ?? "MXN");
  const [validaHasta, setValidaHasta] = useState(cotizacion?.validaHasta ?? "");
  const [condicionesDePago, setCondicionesDePago] = useState(
    cotizacion?.condicionesDePago ?? "",
  );
  const [notas, setNotas] = useState(cotizacion?.notas ?? "");
  const [conceptos, setConceptos] = useState<ConceptoLocal[]>(
    cotizacion ? conceptosDesdeCotizacion(cotizacion) : [],
  );
  const [errores, setErrores] = useState<Record<string, string>>({});

  function enviar(e: FormEvent) {
    e.preventDefault();

    const conceptosPayload = conceptos.map((c, i) => ({
      servicioId: c.servicioId,
      claveProdServ: c.claveProdServ,
      claveUnidad: c.claveUnidad,
      unidad: c.unidad || undefined,
      descripcion: c.descripcion.trim(),
      cantidad: c.cantidad,
      valorUnitario: c.valorUnitario,
      descuento: c.descuento || undefined,
      objetoImp: c.objetoImp,
      tasaIva: c.tasaIva,
      orden: i,
    }));

    const base = {
      titulo: titulo.trim(),
      proyectoId: proyecto?.id,
      moneda,
      validaHasta,
      condicionesDePago: condicionesDePago.trim() || undefined,
      notas: notas.trim() || undefined,
      conceptos: conceptosPayload,
    };

    const datos = cotizacion ? base : { ...base, clienteId: cliente?.id };

    // Se valida con el MISMO esquema del backend. Si pasa aquí, pasa allá.
    const esquema = cotizacion ? cotizacionUpdateSchema : cotizacionCreateSchema;
    const resultado = esquema.safeParse(datos);
    if (!resultado.success) {
      const porCampo: Record<string, string> = {};
      for (const issue of resultado.error.issues) {
        const campo = String(issue.path[0] ?? "");
        if (campo && !porCampo[campo]) porCampo[campo] = issue.message;
      }
      setErrores(porCampo);
      return;
    }

    alEnviar(datos);
  }

  return (
    <form onSubmit={enviar} className="space-y-5 p-5">
      {errorServidor && <Aviso>{errorServidor}</Aviso>}

      {!cotizacion ? (
        <SelectorCliente
          valor={cliente}
          alSeleccionar={(c) => {
            setCliente(c);
            // Los proyectos son de un cliente; cambiar de cliente invalida la
            // elección anterior en lugar de dejar un proyectoId huérfano.
            setProyecto(null);
          }}
          error={errores["clienteId"]}
        />
      ) : (
        <Campo etiqueta="Cliente">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-800/50">
            <span className="font-medium">{cotizacion.cliente.razonSocial}</span>
            <span className="ml-2 font-mono text-xs text-slate-500">
              {cotizacion.cliente.rfc}
            </span>
          </div>
        </Campo>
      )}

      <SelectorProyecto
        clienteId={cotizacion ? cotizacion.clienteId : cliente?.id}
        valor={proyecto}
        alSeleccionar={setProyecto}
      />

      <Campo etiqueta="Título" requerido error={errores["titulo"]}>
        <Entrada
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          error={!!errores["titulo"]}
        />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo etiqueta="Válida hasta" requerido error={errores["validaHasta"]}>
          <Entrada
            type="date"
            value={validaHasta}
            onChange={(e) => setValidaHasta(e.target.value)}
            error={!!errores["validaHasta"]}
          />
        </Campo>
        <Campo etiqueta="Moneda" error={errores["moneda"]}>
          <Seleccion value={moneda} onChange={(e) => setMoneda(e.target.value)}>
            {Object.entries(MONEDA)
              .filter(([clave]) => clave !== "XXX")
              .map(([clave, nombre]) => (
                <option key={clave} value={clave}>
                  {clave} · {nombre}
                </option>
              ))}
          </Seleccion>
        </Campo>
      </div>

      <Campo etiqueta="Condiciones de pago" error={errores["condicionesDePago"]}>
        <Entrada
          value={condicionesDePago}
          onChange={(e) => setCondicionesDePago(e.target.value)}
          placeholder="p. ej. 50% de anticipo, 50% contra entrega"
        />
      </Campo>

      <fieldset className="space-y-3 border-t border-slate-200 pt-4 dark:border-slate-800">
        <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Conceptos
        </legend>
        <EditorConceptos
          value={conceptos}
          onChange={setConceptos}
          moneda={moneda}
          error={errores["conceptos"]}
        />
      </fieldset>

      <Campo etiqueta="Notas" error={errores["notas"]}>
        <AreaTexto rows={3} value={notas} onChange={(e) => setNotas(e.target.value)} />
      </Campo>

      <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
        <Boton type="button" variante="secundario" onClick={alCancelar}>
          Cancelar
        </Boton>
        <Boton type="submit" disabled={enviando}>
          {enviando ? "Guardando…" : cotizacion ? "Guardar cambios" : "Crear cotización"}
        </Boton>
      </div>
    </form>
  );
}
